class SearchModal extends HTMLElement {
  constructor() {
    super()
    this.modalToggle = document.querySelectorAll("[aria-controls='search-modal']")
    this.detailsContainer = this.querySelector("div.header__search")
    this.modalOverlay = this.querySelector("div.header__search__overlay")
    this.modalToggle.forEach((btn) => {
      btn.addEventListener("click", this.onToggleClick.bind(this))
    })

    this.querySelector('button[type="button"]').addEventListener("click", this.closeModal.bind(this))
  }

  initialize() {}

  onToggleClick(event) {
    event.preventDefault()

    this.detailsContainer.hasAttribute("open") ? this.closeModal() : this.openModal(event)
  }

  openModal(event) {
    this.detailsContainer.setAttribute("open", "true")
    this.modalOverlay.addEventListener("click", this.overlayClick.bind(this))
    document.body.classList.add("overflow-hidden")

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])'),
    )
  }

  closeModal(focusToggle = true) {
    // removeTrapFocus(focusToggle ? this.modalToggle : null)
    this.detailsContainer.removeAttribute("open")
    const input = this.detailsContainer.querySelector('input:not([type="hidden"])')
    if (input) input.value = ""
    const predictive = this.detailsContainer.querySelector("#predictive-search")
    if (predictive) {
      predictive.innerHTML = ""
      predictive.classList.remove("predictive-search--visible")
      predictive.style.display = "none"
    }
    this.modalOverlay.removeEventListener("click", () => {})
    document.body.classList.remove("overflow-hidden")
  }

  overlayClick() {
    this.closeModal()
  }
}

customElements.define("search-modal", SearchModal)

/**
 * Header search modal live results. Uses a distinct tag from Dawn's `<predictive-search>`
 * so `assets/predictive-search.js` can register the same name on the search template.
 */
class HeaderPredictiveSearch extends HTMLElement {
  constructor() {
    super()

    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector("#predictive-search")
    this.cachedResults = {}
    this.abortController = null
    this.minQueryLength = 2

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange(event)
      }, 300).bind(this),
    )
    this.input.addEventListener("keydown", this.onKeydown.bind(this))
    this.input.addEventListener("keyup", this.onKeyup.bind(this))
  }

  onChange() {
    const searchTerm = this.input.value.trim()

    if (searchTerm.length < this.minQueryLength) {
      this.abortPending()
      this.close()
      return
    }

    this.getSearchResults(searchTerm)
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault()
    }
  }

  onKeyup(event) {
    if (!this.predictiveSearchResults?.classList.contains("predictive-search--visible")) return

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault()
        this.switchOption("up")
        break
      case "ArrowDown":
        event.preventDefault()
        this.switchOption("down")
        break
      case "Enter":
        if (this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')) {
          event.preventDefault()
          this.selectOption()
        }
        break
    }
  }

  switchOption(direction) {
    const moveUp = direction === "up"
    const selectedElement = this.predictiveSearchResults.querySelector('[aria-selected="true"]')
    const allElements = this.predictiveSearchResults.querySelectorAll(
      '.predictive-search__list-item[role="option"], #predictive-search-option-search-keywords [role="option"]',
    )
    if (!allElements.length) return

    let activeElement = allElements[0]

    if (moveUp && !selectedElement) return

    if (!moveUp && selectedElement) {
      const idx = Array.prototype.indexOf.call(allElements, selectedElement)
      activeElement = allElements[idx + 1] || allElements[0]
    } else if (moveUp) {
      const idx = Array.prototype.indexOf.call(allElements, selectedElement)
      activeElement = allElements[idx - 1] || allElements[allElements.length - 1]
    }

    if (activeElement === selectedElement) return

    activeElement.setAttribute("aria-selected", "true")
    if (selectedElement) selectedElement.setAttribute("aria-selected", "false")
    this.input.setAttribute("aria-activedescendant", activeElement.id || "")
  }

  selectOption() {
    const selected = this.predictiveSearchResults.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    )
    if (selected) selected.click()
  }

  abortPending() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  getCacheKey(searchTerm) {
    return searchTerm.replace(/\s+/g, "-").toLowerCase()
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getCacheKey(searchTerm)

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    this.abortPending()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    const base =
      typeof window !== "undefined" && window.routes && window.routes.predictive_search_url
        ? window.routes.predictive_search_url
        : "/search/suggest"
    const params = new URLSearchParams({
      q: searchTerm,
      "resources[type]": "query,product",
      "resources[limit]": "8",
      "resources[limit_scope]": "each",
      section_id: "predictive-search",
    })

    fetch(`${base}?${params.toString()}`, { signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status)
          this.close()
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html")
        const section = doc.querySelector("#shopify-section-predictive-search")
        if (!section || !this.predictiveSearchResults) {
          this.close()
          return
        }
        const resultsMarkup = section.innerHTML
        this.cachedResults[queryKey] = resultsMarkup
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        if (error?.name === "AbortError") return
        this.close()
        throw error
      })
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.hydrateJdgmBadges(this.predictiveSearchResults)
    this.open()
  }

  open() {
    this.predictiveSearchResults.classList.add("predictive-search--visible")
    this.predictiveSearchResults.style.display = "block"
  }

  close() {
    const selected = this.predictiveSearchResults?.querySelector('[aria-selected="true"]')
    if (selected) selected.setAttribute("aria-selected", "false")
    this.input?.setAttribute("aria-activedescendant", "")
    this.predictiveSearchResults.classList.remove("predictive-search--visible")
    this.predictiveSearchResults.style.display = "none"
  }

  hydrateJdgmBadges(root) {
    if (!root) return
    const badges = root.querySelectorAll(".jdgm-preview-badge")
    if (!badges.length) return

    if (typeof window.jdgm?.customBadge === "function") {
      badges.forEach((el) => {
        window.jdgm.customBadge(el, { product_id: el.dataset.id })
      })
      return
    }

    if (typeof window.jdgm?.customBadges === "function") {
      window.jdgm.customBadges()
    }
  }

  debounce(fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }
}

customElements.define("header-predictive-search", HeaderPredictiveSearch)
